import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Trophy, Eye, EyeOff, Copy, Check, ExternalLink, Loader2, Sparkles,
  Building2, Save, ArrowUpRight,
} from "lucide-react";

export default function Wins() {
  const [settings, setSettings] = useState(null);
  const [leads, setLeads] = useState(null);
  const [headline, setHeadline] = useState("");
  const [subline, setSubline] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savedAt, setSavedAt] = useState(0);

  const refresh = async () => {
    const [s, l] = await Promise.all([
      api.get("/me/wins-page"),
      api.get("/me/wins-leads"),
    ]);
    setSettings(s.data);
    setLeads(l.data);
    setHeadline(s.data.wins_headline || "");
    setSubline(s.data.wins_subline || "");
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const toggle = async (enabled) => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/wins-page", { enabled });
      setSettings((s) => ({ ...s, ...data }));
    } finally { setSaving(false); }
  };

  const saveCopy = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/wins-page", { headline, subline });
      setSettings((s) => ({ ...s, ...data }));
      setSavedAt(Date.now());
    } finally { setSaving(false); }
  };

  const togglePublish = async (leadId, enabled) => {
    await api.patch(`/leads/${leadId}/publish-win`, { enabled });
    refresh();
  };

  // The shareable URL — use /api/share/wins/{token} so social crawlers see
  // proper OG meta tags + a dynamic 1200×630 preview image. Humans get
  // instantly redirected to /wins/{token}.
  const publicUrl = settings?.wins_token
    ? `${window.location.origin}/api/share/wins/${settings.wins_token}`
    : "";

  const copyUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  if (!settings || !leads) {
    return <div className="p-6 lg:p-10"><div className="card text-center py-16 text-[var(--ink-2)]">Loading…</div></div>;
  }

  return (
    <div className="p-6 lg:p-10" data-testid="wins-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5"><Trophy size={11} /> Wins page</div>
          <h1 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight mt-1">Your Win Story</h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
            Publish your Won deals as a tasteful brag wall. Drop the link in your bio, sign emails, or yard signs —
            social proof that converts on autopilot.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm">
            {settings.wins_enabled ? (
              <span className="chip" style={{ background: "#10B98122", color: "#10B981", borderColor: "#10B98155" }} data-testid="wins-status">
                <span className="chip-dot" style={{ background: "#10B981" }} /> Live
              </span>
            ) : (
              <span className="chip" style={{ background: "rgba(15,23,42,0.06)", color: "var(--ink-2)", borderColor: "var(--line)" }} data-testid="wins-status">
                <span className="chip-dot" style={{ background: "#94A3B8" }} /> Off
              </span>
            )}
          </span>
          {publicUrl && (
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-money"
              data-testid="wins-header-open-button"
              title={settings.wins_enabled ? "Open your live wins page" : "Preview (page is currently offline to the public)"}
            >
              <ExternalLink size={14} /> {settings.wins_enabled ? "Open public page" : "Preview"}
            </a>
          )}
          <button
            type="button"
            className={settings.wins_enabled ? "btn-outline" : "btn-money"}
            onClick={() => toggle(!settings.wins_enabled)}
            disabled={saving || (!settings.wins_enabled && settings.won_count === 0)}
            data-testid="wins-toggle-button"
            title={!settings.wins_enabled && settings.won_count === 0 ? "Mark a deal WON first" : ""}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : settings.wins_enabled ? <EyeOff size={14} /> : <Eye size={14} />}
            {settings.wins_enabled ? "Take page offline" : "Publish wins page"}
          </button>
        </div>
      </div>

      {/* Public URL bar */}
      {settings.wins_enabled && publicUrl && (
        <div
          className="card mb-6 flex items-center gap-3 flex-wrap"
          style={{ borderColor: "#10B981", borderWidth: 2 }}
          data-testid="wins-public-url-card"
        >
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#10B98122", color: "#10B981" }}>
            <ExternalLink size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="label-eyebrow">Your public URL</div>
            <code className="font-mono text-sm block truncate mt-0.5" data-testid="wins-public-url-text">{publicUrl}</code>
          </div>
          <a href={publicUrl} target="_blank" rel="noreferrer" className="btn-outline" data-testid="wins-open-button">
            <ArrowUpRight size={13} /> Open
          </a>
          <button type="button" className="btn-outline" onClick={copyUrl} data-testid="wins-copy-button">
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Settings + deal list (left = wider) */}
        <div className="lg:col-span-3 space-y-4">
          <div className="card" data-testid="wins-copy-card">
            <div className="label-eyebrow mb-3 flex items-center gap-1.5"><Sparkles size={11} /> Page copy</div>
            <label className="block mb-3">
              <span className="label-eyebrow block mb-1.5">Headline</span>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. 12 houses in 90 days"
                maxLength={140}
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                data-testid="wins-headline-input"
              />
            </label>
            <label className="block">
              <span className="label-eyebrow block mb-1.5">Subline</span>
              <textarea
                rows={2}
                className="form-input resize-none"
                placeholder="e.g. We pay cash for houses in Atlanta — fast, fair, no agents."
                maxLength={200}
                value={subline}
                onChange={(e) => setSubline(e.target.value)}
                data-testid="wins-subline-input"
              />
            </label>
            <div className="flex items-center gap-2 mt-3">
              <button type="button" className="btn-money" onClick={saveCopy} disabled={saving} data-testid="wins-save-button">
                {saving ? <Loader2 size={14} className="animate-spin" /> : Date.now() - savedAt < 2500 ? <Check size={14} /> : <Save size={14} />}
                {saving ? "Saving…" : Date.now() - savedAt < 2500 ? "Saved!" : "Save copy"}
              </button>
            </div>
          </div>

          <div className="card" data-testid="wins-deal-list-card">
            <div className="label-eyebrow flex items-center gap-1.5 mb-3"><Trophy size={11} /> Your Won deals</div>
            {leads.length === 0 ? (
              <div className="text-sm text-[var(--ink-2)] py-6 text-center">
                Mark a deal <strong>WON</strong> in any lead's Dispo Timeline — it'll appear here for one-click publishing.
              </div>
            ) : (
              <ul className="divide-y divide-[var(--line)]" data-testid="wins-deal-list">
                {leads.map((l) => (
                  <li key={l.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`wins-deal-row-${l.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{l.property_address}</div>
                      <div className="text-xs text-[var(--ink-3)] mt-0.5">
                        {[l.city, l.state].filter(Boolean).join(", ")}
                        {l.estimated_arv ? ` · ARV $${(l.estimated_arv).toLocaleString()}` : ""}
                      </div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!l.published_to_wins}
                        onChange={(e) => togglePublish(l.id, e.target.checked)}
                        className="h-4 w-4"
                        data-testid={`wins-deal-toggle-${l.id}`}
                      />
                      <span>{l.published_to_wins ? "Published" : "Hidden"}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Stats sidebar */}
        <aside className="lg:col-span-2 space-y-4">
          <div className="card text-center">
            <Building2 size={20} className="text-[var(--ink-3)] mx-auto mb-1" />
            <div className="label-eyebrow">Published</div>
            <div className="font-display font-extrabold text-4xl mt-1" data-testid="wins-published-count">{settings.published_count}</div>
            <div className="text-xs text-[var(--ink-3)] mt-1">of {settings.won_count} won deals</div>
          </div>
          <div className="card">
            <div className="label-eyebrow mb-2">How it works</div>
            <ol className="text-sm text-[var(--ink-2)] space-y-2 list-decimal list-inside">
              <li>Close a deal → mark it <strong>WON</strong> in the dispo timeline.</li>
              <li>Toggle <strong>Publish</strong> on the deals you're proud of.</li>
              <li>Hit <strong>Publish wins page</strong> → drop the URL in your bio, signs, sign emails.</li>
              <li>Cash buyers + private sellers see real proof you close. Watch the buyer list fill up.</li>
            </ol>
            <p className="text-xs text-[var(--ink-3)] mt-3 italic">
              No street addresses or buyer/seller details are ever shown publicly — only city, state, ARV, fee, and time-to-close.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
